import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Filter } from 'lucide-react'
import Layout from '../../components/Layout'
import Seo from '../../components/Seo'
import { listingService } from '../../services/listingService'

const Listing = () => {
  const navigate = useNavigate()
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [reloadToken, setReloadToken] = useState(0)
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    pages: 0,
  })

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextSearch = searchInput.trim()
      setSearchTerm((currentSearch) => {
        if (currentSearch !== nextSearch) {
          setPagination((current) => ({ ...current, page: 1 }))
        }
        return nextSearch
      })
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  useEffect(() => {
    let cancelled = false
    const fetchListings = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await listingService.getListings(pagination.page, 20, searchTerm)
        if (cancelled) return
        setListings(response.listings || [])
        setPagination(response.pagination || { page: 1, total: 0, pages: 0 })
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching listings:', error)
          setError('Chyba při načítání inzerátů')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
          setInitialLoading(false)
        }
      }
    }
    fetchListings()
    return () => { cancelled = true }
  }, [pagination.page, searchTerm, reloadToken])

  const handleDelete = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tento inzerát?')) {
      return
    }

    try {
      await listingService.deleteListing(id)
      setReloadToken((current) => current + 1)
    } catch (error) {
      console.error('Error deleting listing:', error)
      alert('Chyba při mazání inzerátu')
    }
  }

  // Create header content for the Layout
  const headerContent = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 sm:gap-4">
        <h1 className="text-lg sm:text-2xl font-semibold text-gray-900">Inzeráty</h1>
      </div>

      {/* Center - Search Bar */}
      <div className="w-full sm:ml-auto sm:w-72">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Hledat ulici nebo čtvrť…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Right - Filter and New Listing buttons */}
      <div className="flex flex-wrap items-center gap-2">
        <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
          <Filter size={16} />
          <span className="hidden sm:inline">Filtry</span>
        </button>
        <button
          onClick={() => navigate('/listings/new')}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm sm:text-base"
        >
          <Plus size={16} className="sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Nový inzerát</span>
          <span className="sm:hidden">Nový</span>
        </button>
      </div>
    </div>
  )

  if (initialLoading) {
    return (
      <Layout headerContent={headerContent}>
        <Seo title="Inzeráty" description="Přehled a správa inzerátů v administraci Realitní Jestřáb." canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
        <div className="flex justify-center items-center h-64">Načítání...</div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout headerContent={headerContent}>
        <Seo title="Inzeráty" description="Přehled a správa inzerátů v administraci Realitní Jestřáb." canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-600 mb-2">{error}</div>
            <button onClick={() => setReloadToken((current) => current + 1)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Zkusit znovu
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout headerContent={headerContent}>
      <Seo title="Inzeráty" description="Přehled a správa inzerátů v administraci Realitní Jestřáb." canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined} />
      {/* Listings List */}
      <div className="px-3 sm:px-4 py-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium text-gray-900">Všechny inzeráty ({pagination.total})</h2>
              {loading && <span className="text-xs text-gray-500">Načítám…</span>}
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {listings.map((listing) => (
              <div
                key={listing._id}
                className="px-4 sm:px-6 py-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                onClick={() => navigate(`/listings/${listing._id}`)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                  <div className="flex-1">
                    <div className="mb-2">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 line-clamp-2">{listing.address}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-gray-600">
                      <span className="bg-gray-100 px-2 py-1 rounded">{listing.type}</span>
                      <span className="bg-gray-100 px-2 py-1 rounded">{listing.layout}</span>
                      <span className="bg-gray-100 px-2 py-1 rounded">{listing.m2} m²</span>
                      {listing.price && <span className="bg-green-100 text-green-800 px-2 py-1 rounded font-medium">{listing.price.toLocaleString()} Kč</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{new Date(listing.createdAt).toLocaleDateString('cs-CZ')}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(listing._id)
                      }}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      title="Smazat"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Předchozí
              </button>
              <span className="px-3 py-2 text-sm text-gray-600">
                Stránka {pagination.page} z {pagination.pages}
              </span>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Další
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Listing
